import { useEffect, useRef } from 'react'

import { useRouter } from '@tanstack/react-router'

import * as Sentry from '@sentry/react'

/**
 * Track navigation changes for Sentry breadcrumbs.
 *
 * This hook subscribes to router state changes and logs navigation events
 */
export function useNavigationTracking() {
	const router = useRouter()
	const previousPathRef = useRef<string | null>(null)

	useEffect(() => {
		// Subscribe to router state changes
		const unsubscribe = router.subscribe('onResolved', () => {
			const currentPath = router.state.location.pathname
			const currentSearch = router.state.location.search

			// Only log if path actually changed
			if (previousPathRef.current !== currentPath) {
				Sentry.addBreadcrumb({
					category: 'navigation',
					message: `Navigated to ${currentPath}`,
					level: 'info',
					data: {
						from: previousPathRef.current,
						to: currentPath,
						search: currentSearch,
					},
				})

				previousPathRef.current = currentPath
			}
		})

		// Set initial path
		previousPathRef.current = router.state.location.pathname

		return unsubscribe
	}, [router])
}

/**
 * Track user actions for Sentry breadcrumbs
 * Use this for critical user actions like delete, submit, confirm
 *
 * @example
 * trackAction('button.click', 'Delete Integration', { integrationId: '123' })
 * trackAction('form.submit', 'Create Playbook', { playbookName: 'My Playbook' })
 */
export function trackAction(
	type:
		| 'button.click'
		| 'form.submit'
		| 'modal.open'
		| 'modal.confirm'
		| 'toggle'
		| 'search',
	message: string,
	data?: Record<string, any>,
) {
	Sentry.addBreadcrumb({
		category: 'user-action',
		message,
		level: 'info',
		type,
		data: {
			...data,
			timestamp: new Date().toISOString(),
		},
	})
}
